import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import {
  Button, Dialog, DialogContent, DialogFooter, DialogTitle, MenuItem, SelectField,
} from '../../2-component';
import { Box, Typography } from '../../1-primative';
import { Divider } from '../../2-component/divider/divider';
import { colors } from '../../0-tokens';
import { useGlobalToast } from '../../../providers/globalToastProvider';
import {
  AffiliationRelations, ClientGroup, Relationship, User,
} from '../../../interfaces';
import { generateClientNameString } from '../../../util';
import { HouseholdSelect } from '../householdSelect/householdSelect';
import { UPDATE_CLIENTGROUP_RELATIONSHIPS } from '../../4-module/widgets/householdMembers/components/tableHouseHoldMembers';

const INITIAL_DATA = {
  name: '',
  household: {
    id: '',
    name: '',
    primaryUser: {
      id: '',
      firstName: '',
      lastName: '',
    },
    relationships: [] as Relationship[],
  },
  relationship: '',
  accessType: '',
};

const ClientToHouseholdDialog = ({
  open, setOpen, user, refetch,
}: { open: boolean; setOpen: (open: boolean) => void; user: User, refetch: () => void }) => {
  const { t } = useTranslation('addClientToHouseholdWizard');
  const { showToast } = useGlobalToast();
  const [data, setData] = useState(INITIAL_DATA);
  const [step, setStep] = useState(1);

  const alreadyMember = !!data.household.relationships.find((r) => r.user.id === user?.id);

  const [updateRelationships, { loading }] = useMutation(UPDATE_CLIENTGROUP_RELATIONSHIPS);

  const onClose = () => {
    setStep(1);
    setOpen(false);
    setData(INITIAL_DATA);
  };

  const goBack = () => {
    setStep(step - 1);
  };

  const handleMemberUpdate = () => {
    const existingRelationships = data.household.relationships.map((r) => ({
      userId: r.user.id,
      type: r.type,
      accessType: r.accessType,
    }));

    const newRelationship = {
      userId: user?.id,
      type: data.relationship,
      accessType: data.accessType,
    };

    updateRelationships({
      variables: {
        clientGroupId: data.household.id,
        relationships: [...existingRelationships, newRelationship],
      },
      onCompleted: () => {
        showToast({
          severity: 'success',
          message: t('clientAddedToHousehold'),
        });
        onClose();
        refetch();
      },
    });
  };

  return (
    <Dialog onClose={onClose} open={open} fullWidth>
      <DialogTitle onClose={onClose}>
        <Box>
          {step === 1 && <Typography variant='headingSmall'>{t('title')}</Typography>}
          {step !== 1 && (
            <Box ml={0} sx={{ cursor: 'pointer' }} onClick={goBack} display='flex' alignItems='center' gap={1}>
              <ArrowBackIosIcon fontSize='small' sx={{ cursor: 'pointer' }} />
              <Typography variant='bodyLarge'>{t('back')}</Typography>
            </Box>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {step === 1 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <HouseholdSelect
              label={t('householdName')}
              autoFocus
              fullWidth
              household={data.household as Partial<ClientGroup>}
              error={alreadyMember}
              errorText={t('alreadyMemberOfHousehold', { householdName: data.household.name })}
              setHousehold={(newValue) => {
                setData({
                  ...data,
                  household: {
                    id: newValue?.id ?? '',
                    name: newValue?.name ?? '',
                    primaryUser: {
                      id: newValue?.primaryUser?.id ?? '',
                      firstName: newValue?.primaryUser?.firstName ?? '',
                      lastName: newValue?.primaryUser?.lastName ?? '',
                    },
                    relationships: newValue?.relationships ?? [],
                  },
                });
              }}
            />
            <SelectField fullWidth value={data.relationship ?? ''} label={t('household:relationModal.relationType')} onChange={(e: any) => setData({ ...data, relationship: e.target.value })}>
              {Object.values(AffiliationRelations).map((relation, index) => (
                <MenuItem key={index} value={relation}>
                  {t(`affiliationTypes:affiliationRelationship.${relation}`)}
                </MenuItem>
              ))}
            </SelectField>
            <SelectField fullWidth label={t('household:relationModal.permission')} onChange={(e: any) => setData({ ...data, accessType: e.target.value })} value={data.accessType ?? ''}>
              <MenuItem value='VIEW'>{t('household:accessTypes.VIEW')}</MenuItem>
              <MenuItem value='NO_ACCESS'>{t('household:accessTypes.NO_ACCESS')}</MenuItem>
            </SelectField>
          </Box>
        ) : (
          <Box m={2}>
            <Typography align='center' variant='headingSmall' mb={1}>
              {t('memberSummary')}
            </Typography>
            <Typography align='center' variant='bodyMedium' mb={2}>
              {t('pleaseReview')}
            </Typography>
            <Divider sx={{ height: 0, borderColor: colors.neutral300 }} />
            <Box sx={styles.listItem}>
              <Typography variant='bodyMedium'>{t('name')}</Typography>
              <Typography variant='bodyMedium'>{generateClientNameString(user)}</Typography>
            </Box>
            <Box sx={styles.listItem}>
              <Typography variant='bodyMedium'>{t('householdName')}</Typography>
              <Typography variant='bodyMedium'>{data.household.name}</Typography>
            </Box>
            <Box sx={styles.listItem}>
              <Typography variant='bodyMedium'>{t('relationship')}</Typography>
              <Typography variant='bodyMedium'>{t(`affiliationTypes:affiliationRelationship.${data.relationship}`)}</Typography>
            </Box>
            <Box sx={styles.listItem}>
              <Typography variant='bodyMedium'>{t('accessType')}</Typography>
              <Typography variant='bodyMedium'>{t(`household:accessTypes.${data.accessType}`)}</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogFooter>
        <Button label={step === 1 ? t('continue') : t('save')} onClick={() => {
          if (step === 1) {
            setStep(2);
          } else {
            handleMemberUpdate();
          }
        }} disabled={!data.household.name || !data.relationship || loading || alreadyMember || !data.accessType} />
      </DialogFooter>
    </Dialog>
  );
};

export default ClientToHouseholdDialog;

const styles = {
  listItem: {
    marginTop: '1em',
    marginBottom: '1em',
    display: 'flex',
    justifyContent: 'space-between',
  },
};
